import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "@/data/content";
import { useLenis } from "@/hooks/useLenis";

export const Navbar = () => {
  const scrollTo = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled || open
          ? "border-b border-[#1B3B2B]/10 bg-[#FAF7F2]/90 shadow-[0_10px_30px_rgba(27,59,43,0.06)] backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          type="button"
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Retour en haut"
        >
          <Logo />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              type="button"
              data-testid={link.testid}
              onClick={() => go(link.href)}
              className="group relative font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-[#1B3B2B]/80 transition-colors duration-300 hover:text-[#1B3B2B]"
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#C85A32] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            type="button"
            data-testid="nav-reserver-button"
            onClick={() => go("#contact")}
            className="rounded-full bg-[#1B3B2B] px-6 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[#F5F0E6] transition-colors duration-300 hover:bg-[#C85A32]"
          >
            Réserver
          </button>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#1B3B2B]/15 text-[#1B3B2B] lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-[#1B3B2B]/10 bg-[#FAF7F2]/95 backdrop-blur-md lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.href}
                  type="button"
                  data-testid={`nav-mobile-${link.testid}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  onClick={() => go(link.href)}
                  className="rounded-xl px-4 py-3 text-left font-display text-2xl font-medium text-[#1B3B2B] transition-colors hover:bg-[#F4EFE6]"
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.button
                type="button"
                data-testid="nav-mobile-reserver-button"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                onClick={() => go("#contact")}
                className="mt-3 rounded-full bg-[#C85A32] px-6 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#FAF7F2]"
              >
                Réserver une table
              </motion.button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
